import { useEffect, useMemo, useState } from "react";
import {
  Action,
  Amount,
  Bill,
  Category,
  DateLabel,
  Details,
  ReimbursementUserDetailsModal,
  Pending,
  primary,
  secondary,
  Status,
  success,
  View,
  ViewMoreDetails,
  warningColor,
  ReimbursementModalBills,
} from "../../../../utils/Constant";
import { dateConversion } from "../../../../utils/dateUtils";
import { P } from "../../../../AbstractElements";
import { useAppDispatch,  } from "../../../../ReduxToolkit/Hooks";
import { Badge } from "reactstrap";
import BadgeDetails from "../../../../CommonElements/Badge/BadgeDetails";
import { openModal } from "../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import IconComponent from "../../../../CommonElements/IconComponent";
import CustomDataTable from "../../../../CommonElements/Table/CustomTable";
import {
  IReimbursementServiceList,
  ReimbursementTableProps,
} from "../../types";
import { useStaggeredRowStyles } from "../../../../hooks/useStaggeredRowStyle";
import { TableColumn } from "react-data-table-component";
import SkeletonTable from "../../../../CommonElements/Loader/SkeletonTableLoader";

const ReimbursementTable = ({
  findUserBill,
  openDialog,
  reimbursementList,
  reimbursementListLoading,
  setReimbursementUserDetails,
}: ReimbursementTableProps) => {
  const dispatch = useAppDispatch();
  const [animate, setAnimate] = useState<boolean>(false);

  const conditionalRowStyles =
    useStaggeredRowStyles<Array<IReimbursementServiceList>>(animate);

  const reImbursementDetailsColumn = useMemo<
    TableColumn<IReimbursementServiceList>[]
  >(() => {
    const baseCols: TableColumn<IReimbursementServiceList>[] = [
      {
        name: "#",
        cell: (row, index: number) => index + 1,
        sortable: true,
        width: "60px",
      },
      {
        name: DateLabel,
        cell: (row) => (
          <P className="m-0 f-12">{dateConversion(row?.date ?? "", true)}</P>
        ), // Employee name
        sortable: true,
        width: "180px",
      },
      {
        name: Category,
        cell: (row) => (
          <div>
            <P className="m-0 f-12">{row.destination}</P>
          </div>
        ),
        sortable: true,
        width: "120px",
      },
      {
        name: Amount,
        cell: (row) => <P className="m-0 f-12">{row?.amount}</P>,
        sortable: true,
        width: "100px",
      },
      {
        name: Bill,
        cell: (row) => {
          return (
            <>
              {row.bill !== "" && (
                <div
                  className="pointer"
                  onClick={() => {
                    findUserBill(Number(row.id));
                    dispatch(
                      openModal({
                        modalType: ReimbursementModalBills,
                      })
                    );
                  }}
                >
                  <BadgeDetails className="f-12" color={primary} text={View} />
                </div>
              )}
            </>
          );
        },
        sortable: true,
        width: "100px",
      },

      {
        name: Status,
        cell: (row) => (
          <div>
            <Badge
              className="text-capitalize f-12"
              color={
                row?.status?.toLowerCase() === Pending ? warningColor : success
              }
            >
              {row.status}
            </Badge>
          </div>
        ),
        sortable: true,
        width: "100px",
      },
      {
        name: Details,
        cell: (row) => (
          <div
            className="pointer"
            onClick={() => {
              setReimbursementUserDetails(row);
              dispatch(openModal({ modalType: ReimbursementUserDetailsModal }));
            }}
          >
            <BadgeDetails
              className="f-12"
              color={secondary}
              text={ViewMoreDetails}
            />
          </div>
        ),
        sortable: true,
        width: "130px",
      },
      {
        name: Action,
        cell: (row) => (
          <div
            {...(row.id !== null &&
              row.id !== undefined && {
                onClick: () => openDialog(row.id as number),
              })}
          >
            <IconComponent className="icofont icofont-ui-delete text-danger f-16 pointer" />
          </div>
        ),
        sortable: true,
        width: "90px",
      },
    ];
    return baseCols;
  }, [reimbursementList]);

  const tableData = useMemo<
    (IReimbursementServiceList & { _rowClass: string })[]
  >(() => {
    return (
      reimbursementList?.map((row, i) => ({
        ...row,
        _rowClass: `row-animate row-delay-${i}`,
      })) ?? []
    );
  }, [reimbursementList]);

  useEffect(() => {
    if (
      !reimbursementListLoading &&
      reimbursementList&&
      reimbursementList.length > 0
    ) {
      const timeout = setTimeout(() => setAnimate(true), 50);
      return () => clearTimeout(timeout);
    }
    setAnimate(false);
  }, [reimbursementList, reimbursementListLoading]);

  return (
    <div className="table-responsive user-datatable custom-scrollbar mt-4 d-none d-lg-block">
      <CustomDataTable
        noHeader
        striped
        fixedHeaderScrollHeight="100vh"
        columns={reImbursementDetailsColumn}
        data={tableData}
        loading={reimbursementListLoading}
        loaderComponent={
          <SkeletonTable
            rows={tableData.length || 8}
            columns={reImbursementDetailsColumn}
            rowHeight={40}
            gap={4}
          />
        }
        conditionalRowStyles={conditionalRowStyles}
      />
    </div>
  );
};

export default ReimbursementTable;
